import {Injectable} from '@angular/core';
import {ToastButton, ToastController} from '@ionic/angular';
import {filter, from, noop, Observable, tap} from 'rxjs';
import { FrontIziviaTranslateService } from '../i18n/iziviaTranslate.service';


export enum MessageType {
    INFO = 'primary',
    SUCCESS = 'primary',
    WARNING = 'warning',
    ERROR = 'danger'
}

@Injectable({
    providedIn: 'root'
})
export class IziviaToastService {

    static DURATION_IN_MS_DEFAULT: number = 4000;
    static DURATION_IN_MS_LONG: number = 6000;
    // the value 0 won't call dismiss ionic method which close the toaster
    static DURATION_NO_TIMEOUT: number = 0;

    constructor(private readonly iziviaTranslateService: FrontIziviaTranslateService,
        private readonly toastController: ToastController) {
    }

    private SERVER_KEY: string = 'server.';
    private toastNumber: number = 0;

    public isToastDisplayed(): boolean {
        return this.toastNumber > 0;
    };

    public showInfo(labelKey: string, options?: IziviaToastOptions): void {
        this.showFromLabelKey(labelKey, MessageType.INFO, options)
            .subscribe();
    }

    public showSuccess(labelKey: string, options?: IziviaToastOptions): void {
        this.showFromLabelKey(labelKey, MessageType.SUCCESS, options)
            .subscribe();
    }

    public showWarning(labelKey: string, options?: IziviaToastOptions): void {
        this.showFromLabelKey(labelKey, MessageType.WARNING, options)
            .subscribe();
    }

    public showWarningOrFallback(labelKey: string, fallbackLabelKey: string, options?: IziviaToastOptions): void {
        this.showFromLabelKeyOrFallbackToDefaultKey(labelKey, fallbackLabelKey, MessageType.WARNING, options);
    }

    public showWarningIfExist(labelKey: string, options?: IziviaToastOptions): void {
        this.showFromLabelKeyIfExist(labelKey, MessageType.WARNING, options);
    }

    public showError(labelKey: string, options?: IziviaToastOptions): void {
        this.showError$(labelKey, options)
            .subscribe();
    }

    public showError$(labelKey: string, options?: IziviaToastOptions): Observable<HTMLIonToastElement> {
        return this.showFromLabelKey(labelKey, MessageType.ERROR, options);
    }

    public showErrorWithParams(labelKey: string,
        params: object,
        options?: IziviaToastOptions): void {
        this.showFromLabelKeyAndParams(labelKey, params, MessageType.ERROR, options);
    }

    /**
     * prefix server key with 'server' string. If a translation of the server message exist then show it,
     * otherwise translate fallbackLabelKey without the server prefix
     * @param serverError mostly get by a http request, the error object or the label string
     * @param fallbackLabelKey use if serverKey have no translation
     * @param options additional toast options
     */
    public showServerErrorOrFallback(serverError: { error: { labelKey: string, inputCode?: string } } | string,
        fallbackLabelKey: string,
        options?: IziviaToastOptions): void {
        let serverLabelKey: string = this.SERVER_KEY;
        let serverLabelKeyParam: object = {};

        if (!!serverError) {
            if (typeof serverError === 'string') {
                serverLabelKey += serverError;
            } else if (typeof serverError === 'object' && serverError.error && serverError.error.labelKey) {
                serverLabelKey += serverError.error.labelKey;
                if (!!serverError.error.inputCode) {
                    serverLabelKeyParam = {inputCode: serverError.error.inputCode};
                }
            }
        }

        this.showErrorWithParamsOrFallback(serverLabelKey, serverLabelKeyParam, fallbackLabelKey, options);
    }

    /**
     * If a translation of the message exist then show it,
     * otherwise translate and show fallbackLabelKey
     * @param errorLabelKey the label string
     * @param fallbackLabelKey use if errorLabelKey have no translation
     * @param options additional toast options
     */
    public showErrorOrFallback(errorLabelKey: string,
        fallbackLabelKey: string,
        options?: IziviaToastOptions) {
        return this.showErrorWithParamsOrFallback(errorLabelKey, {}, fallbackLabelKey, options);
    }

    public showErrorWithParamsOrFallback(errorLabelKey: string,
        params: object,
        fallbackLabelKey: string,
        options?: IziviaToastOptions) {
        let message: string;

        message = this.iziviaTranslateService.translate(errorLabelKey, params);

        // Server error key not found: using fallback
        if (message === errorLabelKey) {
            message = this.iziviaTranslateService.translate(fallbackLabelKey);
            if (!!options) {
                options.header = options.fallbackHeader;
            }
        }

        this.showMessage(message, MessageType.ERROR, options)
            .subscribe();
    }

    public showErrorWithRetryAction$(toastMessageLabelKey: string, retryFn: () => void): Observable<HTMLIonToastElement> {
        const toastOptions: IziviaToastOptions = this.buildRetryToastButton('noTimeout', retryFn);

        return this.showError$(toastMessageLabelKey, toastOptions);
    }

    private buildRetryToastButton(duration: ToastDuration, callback: () => void): IziviaToastOptions {
        const toastOptions = new IziviaToastOptions();
        toastOptions.duration = duration;
        toastOptions.buttons = [{
            text: this.iziviaTranslateService.translate('common.retry'),
            role: 'retry',
            handler: () => callback()
        }];
        return toastOptions;
    }

    private showFromLabelKey(labelKey: string,
        messageType: MessageType = MessageType.INFO,
        options?: IziviaToastOptions): Observable<HTMLIonToastElement> {
        return this.showMessage(this.iziviaTranslateService.translate(labelKey), messageType, options);
    }

    private showFromLabelKeyAndParams(labelKey: string,
        params: object,
        messageType: MessageType = MessageType.INFO,
        options?: IziviaToastOptions): void {
        this.showMessage(this.iziviaTranslateService.translate(labelKey, params), messageType, options)
            .subscribe();
    }

    private showFromLabelKeyIfExist(labelKey: string,
        messageType: MessageType = MessageType.INFO,
        options?: IziviaToastOptions): void {
        const label = this.iziviaTranslateService.translateOrNull(labelKey);
        if (!!label) {
            this.showMessage(label, messageType, options)
                .subscribe();
        }
    }

    /**
     *
     * @param labelKey Label key to translate
     * @param defaultLabelKey Label key to translate if labelKey translation doesn't exist
     * @param messageType Message type
     * @param options Toast options
     * @private
     */
    private showFromLabelKeyOrFallbackToDefaultKey(labelKey: string,
        defaultLabelKey: string,
        messageType: MessageType = MessageType.INFO,
        options?: IziviaToastOptions): void {
        this.showMessage(this.iziviaTranslateService.translateWithDefault(labelKey, defaultLabelKey), messageType, options)
            .subscribe();
    }

    private showMessage(message: string,
        messageType: MessageType = MessageType.INFO,
        options: IziviaToastOptions = new IziviaToastOptions()): Observable<HTMLIonToastElement> {
        let headerLabel = !!options.header ? this.iziviaTranslateService.translate(options.header) : undefined;
        // if label is equals to message then we removed it (this may happen when using fallbackKey)
        if (headerLabel === message) {
            headerLabel = undefined;
        }

        return from(
            this.toastController.create({
                header: headerLabel,
                message: message,
                position: 'bottom',
                duration: options.durationInMs(),
                color: messageType,
                buttons: options.buttons ? options.buttons : [
                    {
                        text: this.iziviaTranslateService.translate('common.close'),
                        role: 'cancel',
                        handler: () => noop()
                    }
                ]
            })
        )
            .pipe(
                filter(() => options.displayOnlyIfNoOtherToastPresent !== true || !this.isToastDisplayed()),
                tap(toast => {
                    toast.present().then(() => this.toastNumber++);
                    toast.onDidDismiss().then(() => this.toastNumber--);
                })
            );

    }
}

export class IziviaToastOptions {
    duration: ToastDuration = 'default';
    header?: string;
    fallbackHeader?: string; // header displayed only when fallback is used
    buttons?: ToastButton[];
    displayOnlyIfNoOtherToastPresent?: boolean;

    durationInMs(): number {
        switch (this.duration) {
            case 'default':
                return IziviaToastService.DURATION_IN_MS_DEFAULT;
            case 'long':
                return IziviaToastService.DURATION_IN_MS_LONG;
            case 'noTimeout':
                return IziviaToastService.DURATION_NO_TIMEOUT;
        }
    }
}

export type ToastDuration = 'default' | 'long' | 'noTimeout';
